import * as yup from 'yup'
import { BLOCK_TYPE } from 'state/registrations'
import { LengthLimitState } from 'state/constants'
import { getStringLength } from 'components/utils'
import { mapValues } from 'lodash'
import { RegistrationBlock } from 'services/api/registrations'
import { TFunction } from 'i18next'
import { ERROR_MESSAGES } from './errorMessages'
import { ERROR_CODES } from 'state/error'
import { STEP_TIME_WINDOW_SCHEMA } from '../validationSchema'
import { BlockField } from '.'
import { CampaignState, CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { ScheduleType } from 'state/registrations'

export const baseValidationSchema = (
    t: TFunction,
    campaign: CampaignState,
    settings: LengthLimitState,
    initialValues: any,
    isExternalUser: boolean,
    tasksEarliestStartAndEndDate: ScheduleType,
) => {
    const isCampaignDraftOrForEdit = [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT].includes(
        campaign?.status,
    )
    return yup.object().shape({
        title: yup
            .string()
            .test(
                'description maximum length',
                t('errorMessage.validation.characterLimitOneHundred'),
                (value: string): boolean => {
                    if (value) {
                        const charLength = getStringLength(value)
                        return !(charLength > settings.registration_form.title)
                    }
                    return true
                },
            )
            .test({
                message: ERROR_MESSAGES['title'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        description: yup
            .string()
            .test(
                'description maximum length',
                t('errorMessage.validation.characterLimitFiveHundred'),
                (value: string): boolean => {
                    if (value) {
                        const charLength = getStringLength(value)
                        return !(charLength > settings.registration_form.description)
                    }
                    return true
                },
            )
            .test({
                message: ERROR_MESSAGES['description'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        time_window: STEP_TIME_WINDOW_SCHEMA(campaign, initialValues, isExternalUser, null, {
            start: tasksEarliestStartAndEndDate.start,
            end: tasksEarliestStartAndEndDate.end,
            isRegistrationTimeComparison: true,
        }),
        approval_expiration_days: yup
            .number()
            .min(1, t('errorMessage.validation.enterNumberGreaterThanZero'))
            .nullable()
            .test({
                message: ERROR_MESSAGES['approval_expiration_days'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: number | null) => {
                    if (isCampaignDraftOrForEdit || value === 0) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        blocksData: yup.lazy((obj: object) =>
            yup.object(
                mapValues(obj, (obj: RegistrationBlock) => {
                    switch (obj.type) {
                        case BLOCK_TYPE.TEXT:
                        case BLOCK_TYPE.NUMBER_SUBMISSION:
                        case BLOCK_TYPE.URL_SUBMISSION:
                            return yup.object({
                                title: yup
                                    .string()
                                    .test(
                                        'title maximum length',
                                        t('errorMessage.validation.characterLimitOneHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.title)
                                            }
                                            return true
                                        },
                                    )
                                    .test({
                                        message: ERROR_MESSAGES['blocksData']['title'][ERROR_CODES.INPUT_REQUIRED],
                                        test: (value: string | null) => {
                                            if (isCampaignDraftOrForEdit) {
                                                return true
                                            } else {
                                                return Boolean(value)
                                            }
                                        },
                                    }),
                                description: yup
                                    .string()
                                    .test(
                                        'description maximum length',
                                        t('errorMessage.validation.characterLimitFiveHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.description)
                                            }
                                            return true
                                        },
                                    )
                                    .nullable(),
                            })
                        case BLOCK_TYPE.PHOTO_SUBMISSION:
                            return yup.object({
                                title: yup
                                    .string()
                                    .test(
                                        'title maximum length',
                                        t('errorMessage.validation.characterLimitOneHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.title)
                                            }
                                            return true
                                        },
                                    )
                                    .test({
                                        message: ERROR_MESSAGES['blocksData']['title'][ERROR_CODES.INPUT_REQUIRED],
                                        test: (value: string | null) => {
                                            if (isCampaignDraftOrForEdit) {
                                                return true
                                            } else {
                                                return Boolean(value)
                                            }
                                        },
                                    }),
                                description: yup
                                    .string()
                                    .test(
                                        'description maximum length',
                                        t('errorMessage.validation.characterLimitFiveHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.description)
                                            }
                                            return true
                                        },
                                    )
                                    .nullable(),
                                data: yup.object({
                                    number_of_photos: yup
                                        .number()
                                        .nullable()
                                        .min(1, t('errorMessage.validation.enterNumberGreaterThanZero'))
                                        .integer(t('errorMessage.validation.enterWholeNumber'))
                                        .test({
                                            message:
                                                ERROR_MESSAGES['blocksData']['data']['number_of_photos'][
                                                    ERROR_CODES.INPUT_REQUIRED
                                                ],
                                            test: (value: number | null) => {
                                                if (isCampaignDraftOrForEdit || value === 0) {
                                                    return true
                                                } else {
                                                    return Boolean(value)
                                                }
                                            },
                                        }),
                                }),
                            })
                        case BLOCK_TYPE.TEXT_SUBMISSION:
                            return yup.object({
                                title: yup
                                    .string()
                                    .test(
                                        'title maximum length',
                                        t('errorMessage.validation.characterLimitOneHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.title)
                                            }
                                            return true
                                        },
                                    )
                                    .test({
                                        message: ERROR_MESSAGES['blocksData']['title'][ERROR_CODES.INPUT_REQUIRED],
                                        test: (value: string | null) => {
                                            if (isCampaignDraftOrForEdit) {
                                                return true
                                            } else {
                                                return Boolean(value)
                                            }
                                        },
                                    }),
                                description: yup
                                    .string()
                                    .test(
                                        'description maximum length',
                                        t('errorMessage.validation.characterLimitFiveHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.description)
                                            }
                                            return true
                                        },
                                    )
                                    .nullable(),
                                data: yup.object({}),
                            })
                        case BLOCK_TYPE.PHOTOS:
                            return yup.object({
                                description: yup
                                    .string()
                                    .test(
                                        'description maximum length',
                                        t('errorMessage.validation.characterLimitFiveHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.description)
                                            }
                                            return true
                                        },
                                    )
                                    .nullable(),
                                images: yup
                                    .array()
                                    .nullable()
                                    .test({
                                        message: ERROR_MESSAGES['blocksData']['images'][ERROR_CODES.INPUT_REQUIRED],
                                        test: (value: string[] | null) => {
                                            if (isCampaignDraftOrForEdit) {
                                                return true
                                            } else {
                                                return Boolean(value && value.length > 0)
                                            }
                                        },
                                    }),
                            })
                        case BLOCK_TYPE.QUESTION:
                            let optionNumber = obj.options.length
                            if ((obj as BlockField).hasOthers) optionNumber++
                            const isRequired = obj.is_required
                            const basicMinRule = yup
                                .number()
                                .nullable()
                                .test({
                                    message:
                                        ERROR_MESSAGES['blocksData']['data']['question_number']['min'][
                                            ERROR_CODES.INPUT_REQUIRED
                                        ],
                                    test: function (this, value: number | null) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            return Boolean(value !== null && value >= 0)
                                        }
                                    },
                                })
                                .integer(
                                    t(
                                        'errorMessage.validation.enterNonNegativeNumbers',
                                        'The number must be an integer',
                                    ),
                                )
                            const requiredMinRule = basicMinRule
                                .min(1, t('errorMessage.validation.enterNumberGreaterThanZero'))
                                .max(
                                    optionNumber,
                                    t('errorMessage.businessLogic.minimumNumberLessThanMultipleChoiceOption'),
                                )
                            return yup.object({
                                title: yup
                                    .string()
                                    .test(
                                        'title maximum length',
                                        t('errorMessage.validation.characterLimitOneHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.title)
                                            }
                                            return true
                                        },
                                    )
                                    .test({
                                        message: ERROR_MESSAGES['blocksData']['title'][ERROR_CODES.INPUT_REQUIRED],
                                        test: (value: string | null) => {
                                            if (isCampaignDraftOrForEdit) {
                                                return true
                                            } else {
                                                return Boolean(value)
                                            }
                                        },
                                    }),
                                description: yup
                                    .string()
                                    .test(
                                        'description maximum length',
                                        t('errorMessage.validation.characterLimitFiveHundred'),
                                        (value: string): boolean => {
                                            if (value) {
                                                const charLength = getStringLength(value)
                                                return !(charLength > settings.blocks.description)
                                            }
                                            return true
                                        },
                                    )
                                    .nullable(),
                                data: yup.object({
                                    question_number: yup.object().when('isMultiAnswer', {
                                        is: true,
                                        then: yup
                                            .object({
                                                min: isRequired ? requiredMinRule : basicMinRule,
                                                max: yup
                                                    .number()
                                                    .nullable()
                                                    .integer(t('errorMessage.validation.enterWholeNumber'))
                                                    .min(1, t('errorMessage.validation.enterNumberGreaterThanZero'))
                                                    .max(
                                                        optionNumber,
                                                        t(
                                                            'errorMessage.businessLogic.maximumNumberLessThanMultipleChoiceOption',
                                                        ),
                                                    )
                                                    .test({
                                                        message:
                                                            ERROR_MESSAGES['blocksData']['data']['question_number'][
                                                                'max'
                                                            ][ERROR_CODES.INPUT_REQUIRED],
                                                        test: function (this, value: number | null) {
                                                            if (isCampaignDraftOrForEdit) {
                                                                return true
                                                            } else {
                                                                return Boolean(value && value >= 0)
                                                            }
                                                        },
                                                    }),
                                            })
                                            .test({
                                                message: t<string>(
                                                    'errorMessage.campaignEditor.textField.maxFollowerLessThanMinFollower',
                                                ),
                                                test: (value: { min: number | null; max: number | null }) =>
                                                    value.max && value.min ? value.max >= value.min : true,
                                            }),
                                    }),
                                }),
                                options: yup.array().of(
                                    yup.object().shape({
                                        option: yup
                                            .string()
                                            .test(
                                                'title maximum length',
                                                t('errorMessage.validation.characterLimitOneHundred'),
                                                (value: string): boolean => {
                                                    if (value) {
                                                        const charLength = getStringLength(value)
                                                        return !(charLength > settings.blocks.option)
                                                    }
                                                    return true
                                                },
                                            )
                                            .test({
                                                message:
                                                    ERROR_MESSAGES['blocksData']['options'][ERROR_CODES.INPUT_REQUIRED],
                                                test: (value: string | null) => {
                                                    if (isCampaignDraftOrForEdit) {
                                                        return true
                                                    } else {
                                                        return Boolean(value)
                                                    }
                                                },
                                            }),
                                    }),
                                ),
                            })
                        default:
                            return yup.object()
                    }
                }),
            ),
        ),
    })
}
